/**
 * The greyed chip standing where a cleared attachment's chip was (0353).
 *
 * The part behind it is a `text` part carrying an English sentence for the
 * MODEL (`cleared-attachment.ts` has the whole argument). That sentence is
 * frozen into a database row at deletion time and could never be
 * translated afterwards, so the PERSON reads this instead, built from the
 * marker in the part's `metadata` and rendered in their own language.
 *
 * Not clickable, and deliberately not an `AttachmentChip` with a "gone"
 * state: there is nothing to open, nothing to move to the Drive and no
 * handle to resolve, and threading an empty attachment through that
 * component's five optional behaviours is how one of them ends up
 * firing on a file that is not there.
 */
import { FileX } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { formatBytesForNotification } from '@papan/types'
import type { ClearedAttachment } from '../core/cleared-attachment.js'

export function ClearedAttachmentChip({ info }: { info: ClearedAttachment }) {
  const { t } = useTranslation('agent')
  // The date as the member's own locale writes it. The marker stores the
  // calendar DAY (`YYYY-MM-DD`), so this is parsed as a plain date and
  // never shifted into yesterday by a timezone.
  const [y, m, d] = info.clearedAt.split('-').map(Number)
  const when = Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)
    ? new Date(y, m - 1, d).toLocaleDateString()
    : info.clearedAt
  const why = info.rule === 'member-manual'
    ? t('clearedAttachment.manual', { date: when })
    : t('clearedAttachment.auto', { date: when })

  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-md border border-dashed themed-border px-1.5 py-0.5 opacity-70"
      // The whole sentence on hover: the chip itself has room for the
      // name and the reason, and the size is what tells the member how
      // much the clearing actually gave back.
      title={`${info.filename} — ${why}${info.sizeBytes > 0 ? ` (${formatBytesForNotification(info.sizeBytes)})` : ''}`}
    >
      <FileX size={10} className="themed-text-muted shrink-0" />
      <span className="themed-text-muted truncate max-w-[120px] text-[10px] line-through">
        {info.filename}
      </span>
      <span className="themed-text-muted text-[10px] shrink-0">{why}</span>
    </div>
  )
}
